// AgGrid SSRM configuration for WorkflowTable.
//
// Key decisions (informed by https://www.ag-grid.com/javascript-data-grid/server-side-model-tree-data/):
//
//  - rowModelType: "serverSide"  — datasource drives all data fetching
//  - treeData: true              — parent workflow → child task rows
//  - isServerSideGroup           — tells AG Grid which rows have children
//  - getServerSideGroupKey       — the key passed back as groupKeys[0] in getRows
//  - autoGroupColumnDef          — replaces the explicit Description column;
//                                  this gives AG Grid the group column it needs
//                                  to wire up the expand chevron (the root cause
//                                  of previous failures was groupDisplayType:"custom"
//                                  which removed this column and broke the wiring)
//  - NO groupDisplayType         — use AG Grid's default single group column

import { AgGridReact } from "@jsq/ds/core";
import { WORKFLOW_TABLE_PAGE_SIZE } from "../../constants";
import { isTaskStatusBlocked } from "../taskStatusConfig";
import { DescriptionInnerRenderer } from "./cellRenderers";
import * as styles from "./styles_vanilla.css";
import type { WorkflowTableRow } from "../types";

export function getGridOptions(
  showExpanded: boolean,
  onTaskClick?: (workflowId: string, taskId: string) => void,
  isGPX = false,
): AgGridReact.AgGridReactProps<WorkflowTableRow>["gridOptions"] {
  return {
    context: { onTaskClick },

    // ── SSRM ──────────────────────────────────────────────────────────────────
    rowModelType: "serverSide",
    pagination: true,
    paginationPageSize: WORKFLOW_TABLE_PAGE_SIZE,
    paginationPageSizeSelector: [20, 50, 100],
    cacheBlockSize: WORKFLOW_TABLE_PAGE_SIZE,
    maxBlocksInCache: 10,

    // ── Tree data (SSRM API — NOT getDataPath which is CSR-only) ──────────────
    treeData: true,
    // isServerSideGroup: which rows show the expand chevron
    isServerSideGroup: (row: WorkflowTableRow) => showExpanded && row.isParentRow === true,
    // getServerSideGroupKey: the value AG Grid passes back as groupKeys[0] in getRows
    getServerSideGroupKey: (row: WorkflowTableRow) => row.id,
    getRowId: (params) => params.data.id,
    groupDefaultExpanded: 0,
    // Drop a group's child cache on collapse so re-expanding refetches the
    // workflow's tasks fresh from the server (matches the old client-side
    // refetch-on-expand behaviour).
    purgeClosedRowNodes: true,

    // ── Auto-group column (Description) ───────────────────────────────────────
    // This replaces the explicit Description column in columnDefs.
    // Having this column is what wires the expand chevron to the SSRM pipeline.
    autoGroupColumnDef: {
      headerName: "Description",
      field: "displayName",
      minWidth: showExpanded ? 360 : 480,
      flex: 1,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: DescriptionInnerRenderer,
      },
      filter: "agTextColumnFilter",
      suppressHeaderMenuButton: false,
      menuTabs: ["filterMenuTab"],
      filterParams: {
        debounceMs: 1000,
        filterOptions: ["contains", "equals"],
        suppressAndOrCondition: true,
        defaultOption: "contains",
      },
      ...(!showExpanded
        ? {
            cellClass: styles.descriptionBorderRight,
            headerClass: styles.descriptionBorderRight,
          }
        : {}),
      // GPX "Assigned to me" (showExpanded=false) gets a checkbox on the
      // Description column — there is no separate ID column in GPX.
      // "All workflows" (showExpanded=true) is a read-only tree view; no
      // checkbox is shown there, matching Canopy's behaviour for that tab.
      // Only parent workflow rows get a checkbox — task sub-rows are not
      // individually selectable.
      ...(isGPX && !showExpanded
        ? {
            checkboxSelection: (params: { data?: { isParentRow?: boolean } }) =>
              !!params.data?.isParentRow,
            headerCheckboxSelection: false,
          }
        : {}),
    },

    // ── Layout ─────────────────────────────────────────────────────────────────
    domLayout: "normal",
    rowHeight: 52,
    suppressCellFocus: true,
    suppressRowHoverHighlight: false,

    // Blocked child rows (BLOCKED / NOT_STARTED) get a special class that
    // removes the hover tint and sets cursor: default — matching the inert
    // treatment in the drawer's TaskList component.
    rowClassRules: {
      [styles.rowBlocked]: (params) =>
        !params.data?.isParentRow && isTaskStatusBlocked(params.data?.status ?? ""),
    },

    // ── Selection ──────────────────────────────────────────────────────────────
    rowSelection: "multiple",
    suppressRowClickSelection: true,

    // ── Defaults ───────────────────────────────────────────────────────────────
    defaultColDef: {
      resizable: true,
      sortable: false,
      suppressHeaderMenuButton: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      minWidth: 80,
    },

    suppressHorizontalScroll: false,
    enableBrowserTooltips: true,
    suppressContextMenu: true,
    maintainColumnOrder: true,

    sideBar: {
      position: "right" as const,
      toolPanels: [
        {
          id: "bulkActions",
          labelDefault: "Bulk Actions",
          labelKey: "bulkActions",
          iconKey: "",
          toolPanel: "bulkActionsPanel",
        },
        {
          id: "export",
          labelDefault: "Export",
          labelKey: "export",
          iconKey: "",
          toolPanel: "exportPanel",
        },
        {
          id: "savedView",
          labelDefault: "Save View",
          labelKey: "savedView",
          iconKey: "",
          toolPanel: "savedViewPanel",
        },
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: false,
            suppressColumnExpandAll: true,
          },
        },
      ],
    },
  };
}
